import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PartPicker } from '@/components/parts/PartPicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useActiveOrgSlug } from '@/lib/activeOrg';
import { useMe } from '@/lib/auth';
import {
  type AvailabilityLine,
  type BomLine,
  type BomRevisionDetail,
  useAddLine,
  useAvailability,
  useBom,
  useDeleteLine,
  useFreezeRevision,
  useNewRevision,
  useRevisionDetail,
  useRevisions,
  useUpdateLine,
} from '@/lib/boms';
import { cn } from '@/lib/cn';
import { type PartListItem } from '@/lib/parts';

type SortKey = 'part_number' | 'mfg_pn' | 'description' | 'quantity' | 'reference';
type SortDirection = 'asc' | 'desc';

export function BomDetailPage() {
  const { bomId: bomIdRaw } = useParams<{ bomId: string }>();
  const bomId = bomIdRaw ? Number(bomIdRaw) : null;

  const me = useMe();
  const orgSlug = useActiveOrgSlug(me.data?.organizations);

  const bom = useBom(orgSlug, bomId);
  const revisions = useRevisions(orgSlug, bomId);

  const [selectedRevId, setSelectedRevId] = useState<number | null>(null);
  useEffect(() => {
    if (revisions.data && revisions.data.length > 0 && selectedRevId === null) {
      setSelectedRevId(revisions.data[revisions.data.length - 1].id);
    }
  }, [revisions.data, selectedRevId]);

  const detail = useRevisionDetail(orgSlug, bomId, selectedRevId);
  const freeze = useFreezeRevision(orgSlug, bomId ?? -1);
  const newRevision = useNewRevision(orgSlug, bomId ?? -1);

  if (!bomId) return <p className="text-stone-500 dark:text-stone-400">Invalid BOM id.</p>;

  const selectedRev = revisions.data?.find((r) => r.id === selectedRevId) ?? null;
  const isFrozen = selectedRev?.frozen_at !== null && selectedRev?.frozen_at !== undefined;
  const latest = revisions.data?.[revisions.data.length - 1];
  const isLatest = latest?.id === selectedRevId;
  const canFreeze = isLatest && !isFrozen;
  const canCreateNewRev =
    revisions.data &&
    revisions.data.length > 0 &&
    revisions.data[revisions.data.length - 1].frozen_at !== null;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/boms" className="text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200">
          ← BOMs
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {bom.data?.name ?? 'Loading…'}
        </h1>
        {bom.data?.description && (
          <p className="mt-1 text-stone-600 dark:text-stone-400">{bom.data.description}</p>
        )}
      </div>

      <div className="flex-wrap items-center gap-2 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-3">
        <span className="text-sm text-stone-500 dark:text-stone-400">Revision:</span>
        <select
          className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-sm"
          value={selectedRevId ?? ''}
          onChange={(e) => setSelectedRevId(Number(e.target.value))}
        >
          {revisions.data?.map((r) => (
            <option key={r.id} value={r.id}>
              Rev {r.revision_label} {r.frozen_at ? '(frozen)' : '(draft)'}
            </option>
          ))}
        </select>
        {selectedRev && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-xs',
              isFrozen
                ? 'bg-stone-200 dark:bg-stone-700 text-stone-700 dark:text-stone-300'
                : 'bg-amber-100 text-amber-800',
            )}
          >
            {isFrozen ? 'frozen' : 'draft'}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {isLatest && !isFrozen && (
            <Link
              to={`/boms/${bomId}/import`}
              className="inline-flex items-center gap-1 rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-1.5 text-sm font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800"
            >
              Import lines…
            </Link>
          )}
          {canFreeze && (
            <Button
              variant="secondary"
              onClick={() => {
                if (confirm('Freeze this revision? Lines become read-only.')) {
                  freeze.mutate(selectedRevId!);
                }
              }}
              disabled={freeze.isPending}
            >
              {freeze.isPending ? 'Freezing…' : 'Freeze revision'}
            </Button>
          )}
          {canCreateNewRev && (
            <Button
              onClick={async () => {
                const result = await newRevision.mutateAsync();
                setSelectedRevId(result.id);
              }}
              disabled={newRevision.isPending}
            >
              {newRevision.isPending ? 'Creating…' : 'New revision'}
            </Button>
          )}
        </span>
      </div>

      {detail.data && bomId && selectedRevId && (
        <LinesTable
          orgSlug={orgSlug}
          bomId={bomId}
          revisionId={selectedRevId}
          revision={detail.data}
          frozen={isFrozen}
        />
      )}

      {detail.data && bomId && selectedRevId && (
        <AvailabilitySection orgSlug={orgSlug} bomId={bomId} revisionId={selectedRevId} />
      )}
    </div>
  );
}

function LinesTable({
  orgSlug,
  bomId,
  revisionId,
  revision,
  frozen,
}: {
  orgSlug: string | undefined;
  bomId: number;
  revisionId: number;
  revision: BomRevisionDetail;
  frozen: boolean;
}) {
  const addLine = useAddLine(orgSlug, bomId, revisionId);
  const deleteLine = useDeleteLine(orgSlug, bomId, revisionId);

  const [pickerSelected, setPickerSelected] = useState<PartListItem | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [refDes, setRefDes] = useState('');

  const [sortKey, setSortKey] = useState<SortKey>('part_number');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [editingLineId, setEditingLineId] = useState<number | null>(null);

  const onSortClick = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedLines = useMemo(
    () => sortLines(revision.lines, sortKey, sortDir),
    [revision.lines, sortKey, sortDir],
  );

  const onAdd = async () => {
    if (!pickerSelected) return;
    const qNum = Math.max(1, Number(quantity) || 1);
    const ref = refDes
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      await addLine.mutateAsync({
        part_id: pickerSelected.id,
        quantity: qNum,
        reference_designators: ref.length ? ref : undefined,
      });
      setPickerSelected(null);
      setQuantity('1');
      setRefDes('');
    } catch {
      /* surfaced below */
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
      <table className="w-full text-left text-sm">
        <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
          <tr>
            <SortHeader label="Part #" k="part_number" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} />
            <SortHeader label="Mfg PN" k="mfg_pn" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} />
            <SortHeader label="Description" k="description" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} />
            <SortHeader label="Ref des" k="reference" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} />
            <SortHeader label="Qty" k="quantity" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} align="right" />
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {sortedLines.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-8 text-center text-stone-500 dark:text-stone-400">
                No lines yet.
              </td>
            </tr>
          )}
          {sortedLines.map((line) =>
            editingLineId === line.id ? (
              <EditableLineRow
                key={line.id}
                orgSlug={orgSlug}
                bomId={bomId}
                revisionId={revisionId}
                line={line}
                onDone={() => setEditingLineId(null)}
              />
            ) : (
              <LineRow
                key={line.id}
                line={line}
                frozen={frozen}
                onEdit={() => setEditingLineId(line.id)}
                onDelete={() => deleteLine.mutate(line.id)}
              />
            ),
          )}
        </tbody>
      </table>

      {!frozen && (
        <div className="space-y-2 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 p-3">
          <div className="grid gap-2 md:grid-cols-[1fr,160px,140px,auto] md:items-end">
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                Part
              </label>
              <PartPicker
                orgSlug={orgSlug}
                selected={pickerSelected}
                onSelect={setPickerSelected}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                Ref designators
              </label>
              <Input
                value={refDes}
                onChange={(e) => setRefDes(e.target.value)}
                placeholder="R1, R2, R3"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                Quantity
              </label>
              <Input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <Button onClick={onAdd} disabled={!pickerSelected || addLine.isPending}>
              {addLine.isPending ? 'Adding…' : 'Add line'}
            </Button>
          </div>
          {addLine.isError && (
            <p className="text-sm text-red-600">{(addLine.error as Error).message}</p>
          )}
        </div>
      )}
    </div>
  );
}

function SortHeader({
  label,
  k,
  sortKey,
  sortDir,
  onClick,
  align,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDirection;
  onClick: (k: SortKey) => void;
  align?: 'right';
}) {
  return (
    <th
      onClick={() => onClick(k)}
      className={cn(
        'cursor-pointer select-none px-3 py-2 hover:text-stone-800 dark:hover:text-stone-100',
        align === 'right' && 'text-right',
      )}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && <span aria-hidden>{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );
}

function sortLines(lines: BomLine[], key: SortKey, dir: SortDirection): BomLine[] {
  // Pull the comparable value out of a line so the comparator stays
  // simple. Nulls sort last regardless of direction (most users expect
  // missing values to fall to the bottom whether ascending or
  // descending).
  const get = (line: BomLine): string | number | null => {
    if (key === 'part_number') return line.part_number;
    if (key === 'mfg_pn') return line.mfg_pn;
    if (key === 'description') return line.description;
    if (key === 'quantity') return line.quantity;
    if (key === 'reference') {
      const refs = line.reference_designators ?? [];
      return refs.length ? refs[0] : null;
    }
    return null;
  };
  const sign = dir === 'asc' ? 1 : -1;
  return [...lines].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1; // null sorts last regardless of direction
    if (vb === null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return sign * (va - vb);
    return sign * String(va).localeCompare(String(vb), undefined, { numeric: true });
  });
}

function LineRow({
  line,
  frozen,
  onEdit,
  onDelete,
}: {
  line: BomLine;
  frozen: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className="border-t border-stone-100 dark:border-stone-800">
      <td className="px-3 py-2 font-mono text-stone-900 dark:text-stone-100">{line.part_number}</td>
      <td className="px-3 py-2 font-mono">{line.mfg_pn ?? '—'}</td>
      <td className="px-3 py-2">
        <span className="block max-w-xs truncate" title={line.description ?? undefined}>
          {line.description ?? '—'}
        </span>
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {line.reference_designators?.join(', ') ?? '—'}
      </td>
      <td className="px-3 py-2 text-right font-mono">{line.quantity}</td>
      <td className="px-3 py-2 text-right">
        {!frozen && (
          <span className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onEdit}
              className="text-xs text-forge-600 hover:text-forge-800 dark:hover:text-forge-300"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="text-xs text-stone-400 dark:text-stone-500 hover:text-red-600"
            >
              Remove
            </button>
          </span>
        )}
      </td>
    </tr>
  );
}

function EditableLineRow({
  orgSlug,
  bomId,
  revisionId,
  line,
  onDone,
}: {
  orgSlug: string | undefined;
  bomId: number;
  revisionId: number;
  line: BomLine;
  onDone: () => void;
}) {
  const update = useUpdateLine(orgSlug, bomId, revisionId);

  // Pre-seed the part picker with a synthetic record so the user sees
  // "[current part] · change" instead of an empty search box. Only the
  // id/part_number/mfg_pn are read by PartPicker's selected-state UI.
  const initialPart = {
    id: line.part_id,
    part_number: line.part_number,
    mfg_pn: line.mfg_pn,
  } as unknown as PartListItem;
  const [picked, setPicked] = useState<PartListItem | null>(initialPart);
  const [qty, setQty] = useState(String(line.quantity));
  const [refDes, setRefDes] = useState(
    (line.reference_designators ?? []).join(', '),
  );

  const onSave = async () => {
    const refs = refDes
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const qNum = Math.max(1, Number(qty) || 1);
    try {
      await update.mutateAsync({
        lineId: line.id,
        part_id: picked?.id ?? line.part_id,
        quantity: qNum,
        reference_designators: refs,
      });
      onDone();
    } catch {
      /* surfaced inline */
    }
  };

  return (
    <tr className="border-t border-stone-100 dark:border-stone-800 bg-amber-50/40">
      <td colSpan={4} className="px-3 py-2">
        <div className="grid gap-2 md:grid-cols-[1fr,1fr]">
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Part
            </label>
            <PartPicker
              orgSlug={orgSlug}
              selected={picked}
              onSelect={setPicked}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Reference designators
            </label>
            <Input
              value={refDes}
              onChange={(e) => setRefDes(e.target.value)}
              placeholder="R1, R2, R3"
            />
          </div>
        </div>
        {update.isError && (
          <p className="mt-1 text-xs text-red-600">
            {(update.error as Error).message}
          </p>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <Input
          type="number"
          min={1}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="ml-auto w-24 text-right"
        />
      </td>
      <td className="px-3 py-2 text-right">
        <span className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onDone}
            className="text-xs text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={update.isPending}
            className="text-xs font-medium text-forge-700 dark:text-forge-400 hover:text-forge-800 dark:hover:text-forge-300 disabled:opacity-50"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </span>
      </td>
    </tr>
  );
}

function AvailabilitySection({
  orgSlug,
  bomId,
  revisionId,
}: {
  orgSlug: string | undefined;
  bomId: number;
  revisionId: number;
}) {
  const [buildQty, setBuildQty] = useState('1');
  const [enabled, setEnabled] = useState(false);
  const qNum = useMemo(() => Math.max(1, Number(buildQty) || 1), [buildQty]);
  const result = useAvailability(orgSlug, bomId, revisionId, qNum, enabled);

  return (
    <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
      <div className="flex-wrap items-end gap-2 border-b border-stone-200 dark:border-stone-800 p-4">
        <div>
          <h2 className="text-lg font-semibold">Can I build this?</h2>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            Compares each line × build qty against on-hand inventory.
          </p>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <label>
            <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Boards to build
            </span>
            <Input
              type="number"
              min={1}
              value={buildQty}
              onChange={(e) => setBuildQty(e.target.value)}
              className="w-32"
            />
          </label>
          <Button onClick={() => setEnabled(true)} disabled={!qNum}>
            Run check
          </Button>
        </div>
      </div>

      {enabled && result.isPending && (
        <p className="px-4 py-6 text-stone-500 dark:text-stone-400">Computing…</p>
      )}
      {enabled && result.isError && (
        <p className="px-4 py-6 text-red-600">{(result.error as Error).message}</p>
      )}
      {result.data && (
        <AvailabilityResultsTable result={result.data} />
      )}
    </div>
  );
}

function AvailabilityResultsTable({ result }: { result: ReturnType<typeof useAvailability>['data'] & {} }) {
  if (!result) return null;
  return (
    <div>
      <div
        className={cn(
          'flex items-center justify-between px-4 py-2 text-sm',
          result.can_build ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-900',
        )}
      >
        <span>
          {result.can_build
            ? `Yes — you have everything needed for ${result.build_qty} boards.`
            : `Short on ${result.shortage_count} part(s) for ${result.build_qty} boards.`}
        </span>
      </div>
      <table className="w-full text-left text-sm">
        <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
          <tr>
            <th className="px-3 py-2">Part #</th>
            <th className="px-3 py-2">Mfg PN</th>
            <th className="px-3 py-2 text-right">Per board</th>
            <th className="px-3 py-2 text-right">Needed</th>
            <th className="px-3 py-2 text-right">On hand</th>
            <th className="px-3 py-2 text-right">Short</th>
          </tr>
        </thead>
        <tbody>
          {result.lines.map((line: AvailabilityLine) => (
            <tr
              key={line.line_id}
              className={cn(
                'border-t border-stone-100 dark:border-stone-800',
                line.shortfall > 0 && 'bg-red-50',
              )}
            >
              <td className="px-3 py-2 font-mono">{line.part_number}</td>
              <td className="px-3 py-2 font-mono">{line.mfg_pn ?? '—'}</td>
              <td className="px-3 py-2 text-right font-mono">{line.quantity_per_board}</td>
              <td className="px-3 py-2 text-right font-mono">{line.quantity_needed}</td>
              <td className="px-3 py-2 text-right font-mono">
                {line.quantity_on_hand}
                {line.quantity_is_approximate && '+'}
              </td>
              <td
                className={cn(
                  'px-3 py-2 text-right font-mono',
                  line.shortfall > 0 ? 'font-semibold text-red-700' : 'text-stone-400 dark:text-stone-500',
                )}
              >
                {line.shortfall || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
